// src/pages/configuracion/AutomatizacionesPage.tsx
import { useState, useEffect, useCallback } from 'react';
import {
  Zap, Plus, Pencil, Trash2, Play, Pause, History,
  X, Check, AlertTriangle,
  Clock, Ticket, MessageSquare, ArrowUpCircle, UserPlus,
  Bell, Mail, Shield,
} from 'lucide-react';
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  automatizacionesService,
  type AutomatizacionListItem,
  type AutomatizacionEjecucion,
  type AutomatizacionCreate,
  type AutomatizacionUpdate,
  type TriggerTipo,
  type Condicion,
  type Accion,
} from '@/services/automatizaciones';

// ============================================================
// CONSTANTS
// ============================================================

const TRIGGER_OPTIONS: { value: TriggerTipo; label: string; icon: typeof Ticket; desc: string }[] = [
  { value: 'TICKET_CREADO', label: 'Ticket creado', icon: Ticket, desc: 'Se dispara al crear un ticket nuevo' },
  { value: 'TICKET_SIN_RESPUESTA', label: 'Ticket sin respuesta', icon: Clock, desc: 'Se dispara tras N horas sin primera respuesta' },
  { value: 'TICKET_REABIERTO', label: 'Ticket reabierto', icon: ArrowUpCircle, desc: 'Se dispara cuando un ticket se reabre' },
  { value: 'CONVERSACION_NUEVA', label: 'Conversacion nueva', icon: MessageSquare, desc: 'Se dispara al iniciar una conversacion' },
];

const CAMPO_OPTIONS = [
  { value: 'prioridad', label: 'Prioridad' },
  { value: 'tipo', label: 'Tipo' },
  { value: 'estado', label: 'Estado' },
  { value: 'cliente_id', label: 'Cliente ID' },
  { value: 'modulo_id', label: 'Modulo ID' },
  { value: 'asignado_a_id', label: 'Agente asignado' },
];

const OPERADOR_OPTIONS = [
  { value: 'igual', label: 'es igual a' },
  { value: 'no_igual', label: 'no es igual a' },
  { value: 'en_lista', label: 'esta en' },
  { value: 'existe', label: 'existe' },
  { value: 'no_existe', label: 'no existe' },
];

const ACCION_OPTIONS = [
  { value: 'CAMBIAR_PRIORIDAD', label: 'Cambiar prioridad', icon: ArrowUpCircle },
  { value: 'ASIGNAR_AGENTE', label: 'Asignar agente', icon: UserPlus },
  { value: 'ENVIAR_NOTIFICACION', label: 'Enviar notificacion', icon: Bell },
  { value: 'ENVIAR_EMAIL', label: 'Enviar email', icon: Mail },
  { value: 'ESCALAR', label: 'Escalar ticket', icon: Shield },
];

const PRIORIDAD_OPTIONS = ['CRITICA', 'ALTA', 'MEDIA', 'BAJA'];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function AutomatizacionesPage() {
  const [automatizaciones, setAutomatizaciones] = useState<AutomatizacionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [ejecuciones, setEjecuciones] = useState<AutomatizacionEjecucion[]>([]);

  const fetchAutomatizaciones = useCallback(async () => {
    try {
      setLoading(true);
      const data = await automatizacionesService.list();
      setAutomatizaciones(data);
      setError(null);
    } catch (err) {
      setError('Error cargando automatizaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAutomatizaciones();
  }, [fetchAutomatizaciones]);

  const handleToggle = async (id: string, activo: boolean) => {
    try {
      await automatizacionesService.update(id, { activo: !activo });
      await fetchAutomatizaciones();
    } catch {
      setError('Error actualizando automatizacion');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Eliminar esta automatizacion?')) return;
    try {
      await automatizacionesService.delete(id);
      await fetchAutomatizaciones();
    } catch {
      setError('Error eliminando automatizacion');
    }
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleFormSave = async () => {
    await fetchAutomatizaciones();
    handleFormClose();
  };

  const handleViewLog = async (id: string) => {
    if (expandedLog === id) {
      setExpandedLog(null);
      return;
    }
    try {
      const data = await automatizacionesService.getEjecuciones(id);
      setEjecuciones(data);
      setExpandedLog(id);
    } catch {
      setError('Error cargando ejecuciones');
    }
  };

  const getTriggerInfo = (trigger: TriggerTipo) =>
    TRIGGER_OPTIONS.find(t => t.value === trigger);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Automatizaciones
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Reglas que se ejecutan automaticamente cuando ocurren eventos en el sistema
          </p>
        </div>
        <button
          onClick={() => { setEditingId(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <Plus className="h-4 w-4" />
          Nueva automatizacion
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-1/4" />
            </Card>
          ))}
        </div>
      ) : automatizaciones.length === 0 ? (
        <Card className="p-12 text-center">
          <Zap className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-gray-900 font-medium">Sin automatizaciones</h3>
          <p className="text-sm text-gray-500 mt-1">
            Crea tu primera regla para automatizar flujos de trabajo
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {automatizaciones.map(auto => {
            const triggerInfo = getTriggerInfo(auto.trigger);
            const TriggerIcon = triggerInfo?.icon || Zap;

            return (
              <div key={auto.id}>
                <Card className={cn(
                  'p-4 transition-all',
                  !auto.activo && 'opacity-60'
                )}>
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: info */}
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={cn(
                        'p-2 rounded-lg shrink-0',
                        auto.activo ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-400'
                      )}>
                        <TriggerIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{auto.nombre}</h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                            {triggerInfo?.label || auto.trigger}
                          </span>
                          {auto.ejecuciones_count > 0 && (
                            <span>{auto.ejecuciones_count} ejecuciones</span>
                          )}
                          {auto.ultima_ejecucion && (
                            <span>Ultima: {new Date(auto.ultima_ejecucion).toLocaleDateString('es-MX')}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleViewLog(auto.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Ver ejecuciones"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(auto.id)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggle(auto.id, auto.activo)}
                        className={cn(
                          'p-2 rounded-lg transition-colors',
                          auto.activo
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        )}
                        title={auto.activo ? 'Desactivar' : 'Activar'}
                      >
                        {auto.activo ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(auto.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </Card>

                {/* Execution log (expanded) */}
                {expandedLog === auto.id && (
                  <Card className="mt-1 p-4 bg-gray-50 border-dashed">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <History className="h-4 w-4" />
                      Ultimas ejecuciones
                    </h4>
                    {ejecuciones.length === 0 ? (
                      <p className="text-sm text-gray-500">Sin ejecuciones registradas</p>
                    ) : (
                      <div className="space-y-2">
                        {ejecuciones.slice(0, 10).map(ej => (
                          <div
                            key={ej.id}
                            className="flex items-center justify-between text-xs bg-white p-2 rounded border"
                          >
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium',
                                ej.estado === 'EXITOSA' && 'bg-green-100 text-green-700',
                                ej.estado === 'FALLIDA' && 'bg-red-100 text-red-700',
                                ej.estado === 'PARCIAL' && 'bg-amber-100 text-amber-700',
                              )}>
                                {ej.estado === 'EXITOSA' && <Check className="h-3 w-3" />}
                                {ej.estado === 'FALLIDA' && <X className="h-3 w-3" />}
                                {ej.estado === 'PARCIAL' && <AlertTriangle className="h-3 w-3" />}
                                {ej.estado}
                              </span>
                              <span className="text-gray-500">
                                {ej.recurso_tipo}/{ej.recurso_id.slice(0, 8)}...
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-400">
                              {ej.duracion_ms != null && <span>{ej.duracion_ms}ms</span>}
                              <span>{new Date(ej.ejecutada_at).toLocaleString('es-MX')}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <AutomatizacionForm
          editingId={editingId}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
}

// ============================================================
// FORM COMPONENT
// ============================================================

function AutomatizacionForm({
  editingId,
  onClose,
  onSave,
}: {
  editingId: string | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(!!editingId);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [trigger, setTrigger] = useState<TriggerTipo>('TICKET_CREADO');
  const [logica, setLogica] = useState<'AND' | 'OR'>('AND');
  const [condiciones, setCondiciones] = useState<Condicion[]>([]);
  const [acciones, setAcciones] = useState<Accion[]>([
    { tipo: 'CAMBIAR_PRIORIDAD', config: { nueva_prioridad: 'ALTA' } }
  ]);
  const [horas, setHoras] = useState(4);
  const [orden, setOrden] = useState(0);

  useEffect(() => {
    if (!editingId) return;
    (async () => {
      try {
        const data = await automatizacionesService.get(editingId);
        setNombre(data.nombre);
        setDescripcion(data.descripcion || '');
        setTrigger(data.trigger);
        setLogica(data.condiciones?.logica || 'AND');
        setCondiciones(data.condiciones?.condiciones || []);
        setAcciones(data.acciones || []);
        setHoras(data.config_temporal?.horas || 4);
        setOrden(data.orden);
      } catch {
        setError('Error cargando automatizacion');
      } finally {
        setFormLoading(false);
      }
    })();
  }, [editingId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload: AutomatizacionCreate = {
      nombre,
      descripcion: descripcion || undefined,
      trigger,
      condiciones: { logica, condiciones },
      acciones,
      config_temporal: trigger === 'TICKET_SIN_RESPUESTA' ? { horas } : undefined,
      orden,
    };

    try {
      if (editingId) {
        await automatizacionesService.update(editingId, payload as AutomatizacionUpdate);
      } else {
        await automatizacionesService.create(payload);
      }
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error guardando');
    } finally {
      setLoading(false);
    }
  };

  // Condition helpers
  const addCondicion = () => {
    setCondiciones([...condiciones, { campo: 'prioridad', operador: 'igual', valor: '' }]);
  };

  const removeCondicion = (index: number) => {
    setCondiciones(condiciones.filter((_, i) => i !== index));
  };

  const updateCondicion = (index: number, field: keyof Condicion, value: unknown) => {
    const updated = [...condiciones];
    updated[index] = { ...updated[index], [field]: value };
    setCondiciones(updated);
  };

  // Action helpers
  const addAccion = () => {
    setAcciones([...acciones, { tipo: 'CAMBIAR_PRIORIDAD', config: { nueva_prioridad: 'ALTA' } }]);
  };

  const removeAccion = (index: number) => {
    setAcciones(acciones.filter((_, i) => i !== index));
  };

  const updateAccion = (index: number, tipo: string) => {
    const updated = [...acciones];
    const defaultConfigs: Record<string, Record<string, unknown>> = {
      CAMBIAR_PRIORIDAD: { nueva_prioridad: 'ALTA' },
      ASIGNAR_AGENTE: { agente_id: '', sobrescribir: false },
      ENVIAR_NOTIFICACION: { destinatarios: 'agente_asignado', mensaje: '' },
      ENVIAR_EMAIL: { destinatario: 'agente_asignado', asunto: '' },
      ESCALAR: { notificar_a: [] },
    };
    updated[index] = { tipo, config: defaultConfigs[tipo] || {} };
    setAcciones(updated);
  };

  const updateAccionConfig = (index: number, key: string, value: unknown) => {
    const updated = [...acciones];
    updated[index] = {
      ...updated[index],
      config: { ...updated[index].config, [key]: value },
    };
    setAcciones(updated);
  };

  if (formLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <Card className="w-full max-w-2xl p-8 text-center">
          <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto" />
          <p className="mt-3 text-sm text-gray-500">Cargando...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8">
      <Card className="w-full max-w-2xl mx-4">
        {/* Modal header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {editingId ? 'Editar automatizacion' : 'Nueva automatizacion'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Name & description */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input
                type="text"
                value={nombre}
                onChange={e => setNombre(e.target.value)}
                required
                maxLength={200}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Ej: Escalar tickets criticos"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Orden</label>
              <input
                type="number"
                value={orden}
                onChange={e => setOrden(parseInt(e.target.value) || 0)}
                min={0}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
            <textarea
              value={descripcion}
              onChange={e => setDescripcion(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
              placeholder="Descripcion opcional de lo que hace esta regla"
            />
          </div>

          {/* Trigger */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Trigger (cuando)</label>
            <div className="grid grid-cols-2 gap-2">
              {TRIGGER_OPTIONS.map(opt => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTrigger(opt.value)}
                    className={cn(
                      'flex items-start gap-2 p-3 rounded-lg border text-left text-sm transition-all',
                      trigger === opt.value
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Icon className={cn(
                      'h-4 w-4 mt-0.5 shrink-0',
                      trigger === opt.value ? 'text-blue-600' : 'text-gray-400'
                    )} />
                    <div>
                      <div className="font-medium text-gray-900">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Temporal config */}
          {trigger === 'TICKET_SIN_RESPUESTA' && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <label className="block text-sm font-medium text-amber-800 mb-1">
                Horas sin respuesta
              </label>
              <input
                type="number"
                value={horas}
                onChange={e => setHoras(parseInt(e.target.value) || 1)}
                min={1}
                max={720}
                className="w-24 px-3 py-1.5 border rounded-lg text-sm"
              />
              <span className="ml-2 text-sm text-amber-700">horas</span>
            </div>
          )}

          {/* Conditions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Condiciones (si)</label>
              <div className="flex items-center gap-2">
                <select
                  value={logica}
                  onChange={e => setLogica(e.target.value as 'AND' | 'OR')}
                  className="text-xs border rounded px-2 py-1"
                >
                  <option value="AND">Todas (AND)</option>
                  <option value="OR">Alguna (OR)</option>
                </select>
                <button
                  type="button"
                  onClick={addCondicion}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Agregar condicion
                </button>
              </div>
            </div>

            {condiciones.length === 0 ? (
              <p className="text-xs text-gray-400 italic p-2 border border-dashed rounded-lg text-center">
                Sin condiciones — la automatizacion se aplicara a todos los eventos de este trigger
              </p>
            ) : (
              <div className="space-y-2">
                {condiciones.map((cond, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <select
                      value={cond.campo}
                      onChange={e => updateCondicion(i, 'campo', e.target.value)}
                      className="text-xs border rounded px-2 py-1.5 bg-white"
                    >
                      {CAMPO_OPTIONS.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                    <select
                      value={cond.operador}
                      onChange={e => updateCondicion(i, 'operador', e.target.value)}
                      className="text-xs border rounded px-2 py-1.5 bg-white"
                    >
                      {OPERADOR_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {cond.operador !== 'existe' && cond.operador !== 'no_existe' && (
                      <input
                        type="text"
                        value={String(cond.valor || '')}
                        onChange={e => updateCondicion(i, 'valor', e.target.value)}
                        placeholder="Valor"
                        className="flex-1 text-xs border rounded px-2 py-1.5"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeCondicion(i)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Acciones (entonces)</label>
              <button
                type="button"
                onClick={addAccion}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                + Agregar accion
              </button>
            </div>

            <div className="space-y-2">
              {acciones.map((accion, i) => {
                const accionInfo = ACCION_OPTIONS.find(a => a.value === accion.tipo);
                const AccionIcon = accionInfo?.icon || Zap;

                return (
                  <div key={i} className="p-3 bg-gray-50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <AccionIcon className="h-4 w-4 text-gray-400 shrink-0" />
                      <select
                        value={accion.tipo}
                        onChange={e => updateAccion(i, e.target.value)}
                        className="text-sm border rounded px-2 py-1.5 bg-white flex-1"
                      >
                        {ACCION_OPTIONS.map(a => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeAccion(i)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Config fields per action type */}
                    {accion.tipo === 'CAMBIAR_PRIORIDAD' && (
                      <select
                        value={String(accion.config.nueva_prioridad || '')}
                        onChange={e => updateAccionConfig(i, 'nueva_prioridad', e.target.value)}
                        className="text-xs border rounded px-2 py-1.5 bg-white"
                      >
                        {PRIORIDAD_OPTIONS.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    )}

                    {accion.tipo === 'ASIGNAR_AGENTE' && (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={String(accion.config.agente_id || '')}
                          onChange={e => updateAccionConfig(i, 'agente_id', e.target.value)}
                          placeholder="UUID del agente"
                          className="flex-1 text-xs border rounded px-2 py-1.5"
                        />
                        <label className="flex items-center gap-1 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={!!accion.config.sobrescribir}
                            onChange={e => updateAccionConfig(i, 'sobrescribir', e.target.checked)}
                            className="rounded"
                          />
                          Sobrescribir
                        </label>
                      </div>
                    )}

                    {accion.tipo === 'ENVIAR_NOTIFICACION' && (
                      <div className="space-y-2">
                        <select
                          value={String(accion.config.destinatarios || '')}
                          onChange={e => updateAccionConfig(i, 'destinatarios', e.target.value)}
                          className="text-xs border rounded px-2 py-1.5 bg-white"
                        >
                          <option value="agente_asignado">Agente asignado</option>
                          <option value="creador_ticket">Creador del ticket</option>
                          <option value="agentes_especificos">Agentes especificos</option>
                        </select>
                        <input
                          type="text"
                          value={String(accion.config.mensaje || '')}
                          onChange={e => updateAccionConfig(i, 'mensaje', e.target.value)}
                          placeholder="Mensaje de la notificacion"
                          className="w-full text-xs border rounded px-2 py-1.5"
                        />
                      </div>
                    )}

                    {accion.tipo === 'ENVIAR_EMAIL' && (
                      <div className="space-y-2">
                        <select
                          value={String(accion.config.destinatario || '')}
                          onChange={e => updateAccionConfig(i, 'destinatario', e.target.value)}
                          className="text-xs border rounded px-2 py-1.5 bg-white"
                        >
                          <option value="agente_asignado">Agente asignado</option>
                          <option value="cliente">Cliente</option>
                          <option value="email_especifico">Email especifico</option>
                        </select>
                        <input
                          type="text"
                          value={String(accion.config.asunto || '')}
                          onChange={e => updateAccionConfig(i, 'asunto', e.target.value)}
                          placeholder="Asunto del email"
                          className="w-full text-xs border rounded px-2 py-1.5"
                        />
                      </div>
                    )}

                    {accion.tipo === 'ESCALAR' && (
                      <p className="text-xs text-gray-500">
                        Sube la prioridad a CRITICA y notifica a los administradores
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Submit */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !nombre || acciones.length === 0}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear automatizacion'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
